import { Link } from '@tanstack/react-router'
import { Avatar, AvatarFallback } from '@journ/ui/avatar'
import { tv } from '@journ/ui/cn'
import type { TopicView } from '../domain/chat'
import { topicInitials, topicTitle } from './topic-display'
import { UnreadBadge } from './unread-badge'

type ConversationRowProps = {
	topic: TopicView
	active: boolean
	fallbackTitle: string
	// Kept for callers that want a side effect; the row itself navigates via Link.
	onSelect?: (id: string) => void
}

const row = tv({
	base: 'flex w-full items-center gap-3 px-2 py-2 text-left transition-colors hover:bg-muted/60',
	variants: {
		active: {
			true: 'bg-muted/40 text-foreground',
			false: 'text-muted-foreground',
		},
	},
})

const ConversationRow = ({ topic, active, fallbackTitle, onSelect }: ConversationRowProps) => {
	const title = topicTitle(topic, fallbackTitle)
	return (
		<Link
			to='/chat/$topic-id'
			params={{ 'topic-id': topic.id }}
			onClick={() => onSelect?.(topic.id)}
			className={row({ active })}
		>
			<Avatar className='h-9 w-9 shrink-0 rounded-md'>
				<AvatarFallback className='rounded-md text-xs font-semibold'>{topicInitials(title)}</AvatarFallback>
			</Avatar>
			<div className='flex min-w-0 flex-1 flex-col'>
				<span className='truncate text-sm font-medium text-foreground'>{title}</span>
				{topic.unread > 0 && <span className='text-xs text-muted-foreground'>{topic.unread} new</span>}
			</div>
			<UnreadBadge count={topic.unread} />
		</Link>
	)
}

export { ConversationRow }
